import { COLOR_BLUE_800 } from '@eleveo/foundation';
import { CheckmarkIcon, ClockIcon, CrossIcon, NegativeIcon, PalmIcon, SwitchPersonIcon } from '@eleveo/icons-react';
import React from 'react';

import { RequestState, RequestSubtype, RequestType, ShiftSwapRequestController } from '../graphql/components';

type RequestProps = {
  icon: JSX.Element;
  color: string;
  type: RequestType;
  subtype: RequestSubtype;
  nameKey: string;
};

// Request type props

export const VACATION_REQUEST_PROPS: RequestProps = {
  icon: <PalmIcon className="text-grey-600" />,
  color: '#fe7800', // not in Eleveo color palette
  type: RequestType.Pto,
  subtype: RequestSubtype.Vacation,
  nameKey: 'schedule.request.requestType.vacation',
} as const;

export const SICK_LEAVE_REQUEST_PROPS: RequestProps = {
  icon: <NegativeIcon className="text-grey-600" />,
  color: '#cdc6aa', // not in Eleveo color palette
  type: RequestType.Pto,
  subtype: RequestSubtype.Sick,
  nameKey: 'schedule.request.requestType.sickLeave',
} as const;

export const REQUESTER_SHIFT_SWAP_REQUEST_PROPS: RequestProps = {
  icon: <SwitchPersonIcon />,
  color: COLOR_BLUE_800,
  type: RequestType.ShiftSwap,
  subtype: RequestSubtype.Requester,
  nameKey: 'schedule.request.requestType.shiftSwap',
} as const;

export const RECIPIENT_SHIFT_SWAP_REQUEST_PROPS: RequestProps = {
  ...REQUESTER_SHIFT_SWAP_REQUEST_PROPS,
  subtype: RequestSubtype.Recipient,
} as const;

export const APPROVER_SHIFT_SWAP_REQUEST_PROPS: RequestProps = {
  ...REQUESTER_SHIFT_SWAP_REQUEST_PROPS,
  subtype: RequestSubtype.Approver,
} as const;

export const PTO_REQUEST_OPTIONS = [VACATION_REQUEST_PROPS, SICK_LEAVE_REQUEST_PROPS] as const;

export const PTO_AND_SHIFT_SWAP_REQUEST_OPTIONS = [
  VACATION_REQUEST_PROPS,
  SICK_LEAVE_REQUEST_PROPS,
  REQUESTER_SHIFT_SWAP_REQUEST_PROPS,
] as const;

export const PTO_AND_SHIFT_SWAP_REQUEST_SUPERVISOR_OPTIONS = [
  VACATION_REQUEST_PROPS,
  SICK_LEAVE_REQUEST_PROPS,
  APPROVER_SHIFT_SWAP_REQUEST_PROPS,
] as const;

export const REQUESTS_PROPS = {
  [RequestSubtype.Sick]: SICK_LEAVE_REQUEST_PROPS,
  [RequestSubtype.Vacation]: VACATION_REQUEST_PROPS,
  [RequestSubtype.Approver]: APPROVER_SHIFT_SWAP_REQUEST_PROPS,
  [RequestSubtype.Recipient]: RECIPIENT_SHIFT_SWAP_REQUEST_PROPS,
  [RequestSubtype.Requester]: REQUESTER_SHIFT_SWAP_REQUEST_PROPS,
} as Record<RequestSubtype, RequestProps>;

// Requests state props

export const APPROVED_REQUEST_PROPS = {
  icon: <CheckmarkIcon />,
  color: 'success' as const,
  nameKey: 'schedule.request.requestStatus.approved',
  type: RequestState.Approved,
} as const;

export const CANCELED_REQUEST_PROPS = {
  icon: <CrossIcon />,
  color: 'warning' as const,
  nameKey: 'schedule.request.requestStatus.canceled',
  type: RequestState.Canceled,
} as const;

export const PENDING_APPROVAL_REQUEST_PROPS = {
  icon: <ClockIcon />,
  color: 'info' as const,
  nameKey: 'schedule.request.requestStatus.pendingApproval',
  type: RequestState.PendingApproval,
} as const;

export const PENDING_ACCEPTANCE_REQUEST_PROPS = {
  icon: <ClockIcon />,
  color: 'info' as const,
  nameKey: 'schedule.request.requestStatus.pendingAcceptance',
  type: RequestState.PendingAcceptance,
} as const;

export const DECLINED_REQUEST_PROPS = {
  icon: <CrossIcon />,
  color: 'error' as const,
  nameKey: 'schedule.request.requestStatus.declined',
  type: RequestState.Declined,
} as const;

export const REQUEST_STATE_OPTIONS = [
  APPROVED_REQUEST_PROPS,
  CANCELED_REQUEST_PROPS,
  DECLINED_REQUEST_PROPS,
  PENDING_ACCEPTANCE_REQUEST_PROPS,
  PENDING_APPROVAL_REQUEST_PROPS,
];

// Used on supervisor scene only
export const REQUEST_STATE_OPTIONS_TO_FILTER = [
  APPROVED_REQUEST_PROPS,
  CANCELED_REQUEST_PROPS,
  DECLINED_REQUEST_PROPS,
  PENDING_APPROVAL_REQUEST_PROPS,
];

export const REQUEST_STATE_PROPS = {
  [RequestState.Approved]: APPROVED_REQUEST_PROPS,
  [RequestState.Canceled]: CANCELED_REQUEST_PROPS,
  [RequestState.Declined]: DECLINED_REQUEST_PROPS,
  [RequestState.PendingAcceptance]: PENDING_ACCEPTANCE_REQUEST_PROPS,
  [RequestState.PendingApproval]: PENDING_APPROVAL_REQUEST_PROPS,
} as const;

// @ts-expect-error subtypes of PTOs are not expected to be used here
export const SHIFT_SWAP_REQUEST_CONTROLLERS_TO_SUBTYPE_MAP: Record<RequestSubtype, ShiftSwapRequestController> = {
  [RequestSubtype.Approver]: ShiftSwapRequestController.Supervisors,
  [RequestSubtype.Recipient]: ShiftSwapRequestController.Recipients,
  [RequestSubtype.Requester]: ShiftSwapRequestController.Requesters,
} as const;
